import { makeAutoObservable, autorun } from "mobx";
import type { AuthenticationStore } from "../Authentication";
import { AttributeShelf, FetchShelf, InputShelf, LoaderShelf } from "../../shelves";
import { api } from "../../services";
import type { apiTypes } from "../../services";
import { formatCellphoneNumber, formatCEP, invertDate, onlyNumber, switchHyphensToSlashes } from "../../utils/formatters";
import { HasPassword } from "../../enum/password";
import type { FintechRegisterStoreState } from "./interfaces";
import { StoreNameEnum } from "../../enum/storesEnum";
import CryptoJS from "crypto-js";
import { env } from "../../config";

export class FintechRegisterStore {
	public loader = new LoaderShelf();

	public getPersonalFetch: FetchShelf<apiTypes.PersonalCustomer | null>;

	public name = new InputShelf("");
	public email = new InputShelf("");
	public personalPhone = new AttributeShelf<apiTypes.PersonalCustomerContactsPhones | null>(null);
	public hasAlreadyPersonalContactsEmailRegistered = new AttributeShelf(false);
	public personal = new AttributeShelf<apiTypes.PersonalCustomer | null>(null);

	public postalCodeAddressId = new AttributeShelf("");
	public postalCode = new InputShelf("");
	public address = new InputShelf("");
	public districtName = new InputShelf("");
	public number = new InputShelf("");
	public townName = new InputShelf("");
	public countrySubDivision = new InputShelf("");
	public additionalInfo = new InputShelf("");

	public birthDate = new InputShelf("");
	public motherName = new InputShelf("");
	public motherAffiliationId = new AttributeShelf("");
	public successAlterStatusAlreadyRegistration = new AttributeShelf(false);
	public mainEmail = new AttributeShelf("");
	public newEmail = new AttributeShelf("");
	public idEmail = new AttributeShelf("");
	public hasPassword = new AttributeShelf<HasPassword>(HasPassword.FALSE);
	public has4p = new AttributeShelf(false);

	constructor(public authentication: AuthenticationStore) {
		this.getPersonalFetch = new FetchShelf(
			async () => await api.getCustomerPersonal(authentication.getToken() || ""),
			this.setPersonalFields,
			() => {
				throw new Error("");
			},
		);

		makeAutoObservable(this);
		// Carrega o estado salvo do localStorage
		this.loadState();

		// Monitora mudanças e salva o estado automaticamente
		autorun(() => {
			this.saveState();
		});
	}

	// Retorna usuário somente com cadastro na 4p
	public get getHas4p(): boolean {
		return this.has4p.value;
	}

	// Retorna se o usuário já possui uma senha cadastrada
	public get getStatusRegisteredPassword(): HasPassword {
		return this.hasPassword.value;
	}

	// Retorna o valor do email novo inserido no campo
	public get getNewEmail(): string {
		return this.newEmail.value || this.email.field.value;
	}

	public get getMainEmail(): string {
		return this.mainEmail.value;
	}

  public get getPhoneNumber(): string {
    const number = this.personalPhone.value?.number;
    const areaCode = this.personalPhone.value?.areaCode;
    if(areaCode && number) {
      const formattedPhone = formatCellphoneNumber(areaCode + number)

      return formattedPhone;
    }

    return "";
  }

	public setStatusRegisteredPassword(value: HasPassword): void {
		this.hasPassword.set(value);
	}

	public setNewEmail = (value: string): void => {
		this.newEmail.set(value);
	};

	public setMainEmail = (email: string): void => {
		this.mainEmail.set(email);
	};

	public setPersonalFields = (data: apiTypes.PersonalCustomer | null): void => {
		this.personal.set(data);
		if (data) {
			this.hasPassword.set(data.hasPassword);
			this.name.onChange(this.trimString(data.fullName));
			this.has4p.set(data.has4p);

			if (data.birthDate) {
				this.birthDate.onChange(switchHyphensToSlashes(invertDate(data.birthDate)));
			}

			if (data.has4p) {
				const email4p = data.contacts.emails[0].email || "";
				this.email.onChange(this.trimString(email4p));
				this.newEmail.set(this.trimString(email4p));
			} else {
				const mainEmail = data.contacts.emails.find((email) => email.isMain);
				if (mainEmail) {
					this.hasAlreadyPersonalContactsEmailRegistered.set(!!mainEmail.id);
					this.email.onChange(this.trimString(mainEmail.email));
					this.mainEmail.set(this.trimString(mainEmail.email));
					this.idEmail.set(mainEmail.id || "");
				}
			}

			if (this.authentication.currentUser) {
				const { phoneNumber } = this.authentication.currentUser;
				const number = phoneNumber.substring(2);
				const areaCode = phoneNumber.substring(0, 2);

				const tokenPhoneRegistered =
					data.contacts.phones &&
					data.contacts.phones.find((phone) => phone.number === number && phone.areaCode === areaCode);

				if (tokenPhoneRegistered) {
					this.personalPhone.set(tokenPhoneRegistered);
				}
			}

			const mother = data.filiations && data.filiations.find((affiliation) => affiliation.type === "MAE");
			if (mother) {
				this.motherAffiliationId.set(mother.id || "");
				this.motherName.onChange(this.trimString(mother.fullName));
			}

			if (data.address.length > 0) {
				const mainAddress = data.address.find((address) => address.isMain);
				const otherAddress = data.address[0];

				if (mainAddress || otherAddress.postalCode) {
					this.postalCodeAddressId.set(mainAddress?.id || "");
					this.postalCode.onChange(formatCEP(onlyNumber(mainAddress?.postalCode || otherAddress.postalCode)));
					this.address.onChange(this.trimString(mainAddress?.address || otherAddress.address));
					this.districtName.onChange(this.trimString(mainAddress?.districtName || otherAddress.districtName));
					this.number.onChange(mainAddress?.number || otherAddress.number);
					this.townName.onChange(this.trimString(mainAddress?.townName || otherAddress.townName));
					this.countrySubDivision.onChange(
						this.trimString(mainAddress?.countrySubDivision || otherAddress.countrySubDivision),
					);
					this.additionalInfo.onChange(this.trimString(mainAddress?.additionalInfo || otherAddress.additionalInfo));
				}
			}
		}
	};

	// Chama o service para alterar status de already registration
	public fetchAlreadyRegistered = async (): Promise<void> => {
		if (this.loader.isLoading) {
			return;
		}
		this.loader.start();

		try {
			const { alreadyRegistration } = await api.postAlreadyRegistration(this.authentication.getToken() || "");
			this.successAlterStatusAlreadyRegistration.set(alreadyRegistration);
		} catch (error) {
			window.console.error(error);
		} finally {
			this.loader.end();
		}
	};

	// Limpa o estado atual
	public clearState(): void {
		sessionStorage.removeItem(StoreNameEnum.FINTECH_REGISTER);
	}

	// Salva o estado atual no localStorage
	private saveState(): void {
		const state = {
			name: this.trimString(this.name.field.value),
			email: this.trimString(this.email.field.value),
			personalPhone: this.personalPhone.value,
			hasAlreadyPersonalContactsEmailRegistered: this.hasAlreadyPersonalContactsEmailRegistered.value,
			personal: this.personal.value,
			postalCodeAddressId: this.postalCodeAddressId.value,
			postalCode: this.postalCode.field.value,
			address: this.trimString(this.address.field.value),
			districtName: this.trimString(this.districtName.field.value),
			number: this.number.field.value,
			townName: this.trimString(this.townName.field.value),
			countrySubDivision: this.trimString(this.countrySubDivision.field.value),
			additionalInfo: this.trimString(this.additionalInfo.field.value),
			birthDate: this.birthDate.field.value,
			motherName: this.trimString(this.motherName.field.value),
			motherAffiliationId: this.motherAffiliationId.value,
			successAlterStatusAlreadyRegistration: this.successAlterStatusAlreadyRegistration.value,
			mainEmail: this.trimString(this.mainEmail.value),
			newEmail: this.trimString(this.newEmail.value),
			idEmail: this.idEmail.value,
			hasPassword: this.hasPassword.value,
			has4p: this.has4p.value,
		};

		const cipherInfoFintech: string = CryptoJS.AES.encrypt(JSON.stringify(state), env.cryptoKey).toString();
		sessionStorage.setItem(StoreNameEnum.FINTECH_REGISTER, cipherInfoFintech);
	}

	// Carrega o estado salvo do localStorage
	private loadState(): void {
		const savedState = sessionStorage.getItem(StoreNameEnum.FINTECH_REGISTER);
		if (savedState) {
			const bytes = CryptoJS.AES.decrypt(savedState, env.cryptoKey);
			const decryptedData: string = bytes.toString(CryptoJS.enc.Utf8);
			const state: FintechRegisterStoreState = JSON.parse(decryptedData);
			this.name.onChange(state.name);
			this.email.onChange(state.email);
			this.personalPhone.set({
				areaCode: state.personalPhone?.areaCode || "",
				number: state.personalPhone?.number || "",
				countryCode: state.personalPhone?.countryCode || "",
				id: state.personalPhone?.id,
				isMain: state.personalPhone?.isMain,
				provider: state.personalPhone?.provider,
				status: state.personalPhone?.status,
			});
			this.hasAlreadyPersonalContactsEmailRegistered.set(state.hasAlreadyPersonalContactsEmailRegistered);
			this.personal.set(state.personal);
			this.postalCodeAddressId.set(state.postalCodeAddressId);
			this.postalCode.onChange(state.postalCode);
			this.address.onChange(state.address);
			this.districtName.onChange(state.districtName);
			this.number.onChange(state.number);
			this.townName.onChange(state.townName);
			this.countrySubDivision.onChange(state.countrySubDivision);
			this.additionalInfo.onChange(state.additionalInfo);
			this.birthDate.onChange(state.birthDate);
			this.motherName.onChange(state.motherName);
			this.motherAffiliationId.set(state.motherAffiliationId);
			this.successAlterStatusAlreadyRegistration.set(state.successAlterStatusAlreadyRegistration);
			this.mainEmail.set(state.mainEmail);
			this.newEmail.set(state.newEmail);
			this.idEmail.set(state.idEmail);
			this.hasPassword.set(state.hasPassword);
			this.has4p.set(state.has4p);
		}
	}

	// função privada para remover espaços em branco de uma string ou retornar uma string vazia
	private trimString = (value: string | undefined): string => value?.trim() || "";
}
